<template>
  <div class="vp-game">
    <!-- Topbar -->
    <div class="game-topbar">
      <button class="btn-back" @click="router.push({ name: 'player.lobby' })">{{ $t('games.back_to_casino') }}</button>
      <span class="game-title-bar">
        <AppIcon name="game-video-poker" :size="18" /> {{ $t('games.video_poker.title') }}
        <span class="game-tag vp-tag">{{ $t('games.video_poker.tag') }}</span>
      </span>
      <div class="game-topbar-actions">
        <button class="icon-btn" :title="$t('games.provably_fair')" @click="router.push({ name: 'player.provably-fair' })">
          <AppIcon name="provably-fair" :size="16" />
        </button>
      </div>
    </div>

    <!-- Game Body -->
    <div class="game-body">
      <!-- ── Main Area: Card Display ──────────────────────────────────── -->
      <div class="vp-canvas-area">
        <div class="canvas-bg-grid"></div>
        <div class="canvas-bg-glow"></div>

        <div class="vp-display">
          <!-- ── Primary Hand ────────────────────────────────────────── -->
          <div class="vp-primary-hand">
            <div v-if="numHands > 1" class="vp-primary-label">
              {{ $t('games.video_poker.primary_hand') }}
              <span class="vp-hand-badge">{{ $t('games.video_poker.hand_badge', { n: 1, total: numHands }) }}</span>
            </div>

            <!-- 5-card hand -->
            <TransitionGroup name="card-deal" tag="div" class="vp-cards">
              <div
                v-for="(card, idx) in displayHand"
                :key="cardKey(card, idx)"
                class="vp-card-wrap"
              >
                <!-- HOLD indicator (above card) -->
                <div class="vp-hold-label" :class="{ active: isHeld(idx) }">{{ $t('games.video_poker.held') }}</div>

                <!-- Card face -->
                <div
                  class="vp-card"
                  :class="[
                    card ? (isRedSuit(card.suit) ? 'red' : 'black') : 'face-down',
                    { held: phase === 'draw' && isHeld(idx) },
                    { clickable: phase === 'draw' && card !== null },
                  ]"
                  @click="phase === 'draw' && card !== null && toggleHold(idx)"
                >
                  <template v-if="card">
                    <div class="vp-card-corner tl">
                      <span class="vp-rank">{{ rankLabel(card.rank) }}</span>
                      <span class="vp-suit-sm">{{ suitSymbol(card.suit) }}</span>
                    </div>
                    <div class="vp-card-center">{{ suitSymbol(card.suit) }}</div>
                    <div class="vp-card-corner br">
                      <span class="vp-rank">{{ rankLabel(card.rank) }}</span>
                      <span class="vp-suit-sm">{{ suitSymbol(card.suit) }}</span>
                    </div>
                  </template>
                  <template v-else>
                    <!-- Face-down placeholder -->
                    <div class="vp-card-back-pattern"></div>
                  </template>
                </div>
              </div>
            </TransitionGroup>

            <!-- Hand name + win amount (primary hand) -->
            <div v-if="handName" class="vp-hand-name" :class="{ win: isWin }">
              {{ handName }}
            </div>
            <div v-if="numHands === 1 && winAmount > 0 && !gamblingActive" class="vp-win-amount">
              +${{ winAmount.toFixed(2) }}
            </div>
            <div v-else-if="numHands === 1 && phase === 'idle' && lastResult === 'loss'" class="vp-lose-text">
              {{ $t('games.video_poker.no_win') }}
            </div>
          </div>

          <!-- ── Secondary Hands Grid (multi-hand mode) ──────────────── -->
          <template v-if="numHands > 1 && (phase === 'draw' || secondaryHands.length > 0)">
            <div class="vp-multihand-grid" :class="`hands-${numHands}`">
              <div
                v-for="(hand, hIdx) in secondaryHandsDisplay"
                :key="hIdx"
                class="vp-mini-hand"
                :class="{ win: hand.isWin && phase === 'idle' }"
              >
                <!-- Mini hand result badge -->
                <div v-if="phase === 'idle' && hand.handName" class="vp-mini-result" :class="{ win: hand.isWin }">
                  {{ hand.handName }}
                </div>
                <div class="vp-mini-cards">
                  <div
                    v-for="(card, cIdx) in hand.cards"
                    :key="`h${hIdx}-c${cIdx}-${card ? card.rank + card.suit : 'empty'}`"
                    class="vp-mini-card"
                    :class="card ? (isRedSuit(card.suit) ? 'red' : 'black') : 'face-down'"
                  >
                    <template v-if="card">
                      <span class="mini-rank">{{ rankLabel(card.rank) }}</span>
                      <span class="mini-suit">{{ suitSymbol(card.suit) }}</span>
                    </template>
                    <template v-else>
                      <div class="vp-card-back-pattern"></div>
                    </template>
                  </div>
                </div>
                <div v-if="phase === 'idle' && hand.payout > 0" class="vp-mini-payout">
                  +${{ hand.payout.toFixed(2) }}
                </div>
              </div>
            </div>
          </template>

          <!-- Total win for multi-hand -->
          <template v-if="numHands > 1 && phase === 'idle' && lastResult !== null">
            <div v-if="winAmount > 0 && !gamblingActive" class="vp-win-amount multihand-total">
              {{ $t('games.video_poker.total_payout', { amount: winAmount.toFixed(2) }) }}
            </div>
            <div v-else-if="lastResult === 'loss'" class="vp-lose-text">
              {{ $t('games.video_poker.no_win') }}
            </div>
          </template>
        </div>

        <!-- ── Gamble Overlay ──────────────────────────────────────────── -->
        <Transition name="gamble-fade">
          <div v-if="gamblingActive" class="gamble-overlay">
            <div class="gamble-panel">
              <!-- Header -->
              <div class="gamble-header">
                <span class="gamble-title">{{ $t('games.video_poker.double_up') }}</span>
                <span class="gamble-chain-badge">{{ gambleChain + 1 }} / {{ GAMBLE_MAX_CHAIN }}</span>
              </div>

              <!-- Current win at stake -->
              <div class="gamble-stake">
                {{ $t('games.video_poker.gamble_amount', { amount: gambleCurrentWin.toFixed(2) }) }}
              </div>

              <!-- Face-down / revealed card -->
              <div class="gamble-card-area">
                <div
                  class="gamble-card"
                  :class="[
                    gamblePhase === 'revealed' && gambleCard ? (isRedSuit(gambleCard.suit) ? 'red' : 'black') : 'face-down',
                    { 'flip-in': gamblePhase === 'revealing' || gamblePhase === 'revealed' },
                  ]"
                >
                  <template v-if="gamblePhase === 'revealed' && gambleCard">
                    <div class="vp-card-corner tl">
                      <span class="vp-rank">{{ rankLabel(gambleCard.rank) }}</span>
                      <span class="vp-suit-sm">{{ suitSymbol(gambleCard.suit) }}</span>
                    </div>
                    <div class="vp-card-center">{{ suitSymbol(gambleCard.suit) }}</div>
                    <div class="vp-card-corner br">
                      <span class="vp-rank">{{ rankLabel(gambleCard.rank) }}</span>
                      <span class="vp-suit-sm">{{ suitSymbol(gambleCard.suit) }}</span>
                    </div>
                  </template>
                  <template v-else>
                    <div class="vp-card-back-pattern"></div>
                  </template>
                </div>
              </div>

              <!-- Gamble result message -->
              <Transition name="result-pop">
                <div
                  v-if="gamblePhase === 'revealed' && gambleResult !== null"
                  class="gamble-result"
                  :class="gambleResult === 'win' ? 'win' : 'loss'"
                >
                  <span v-if="gambleResult === 'win'">
                    Correct! ${{ gambleCurrentWin.toFixed(2) }} → ${{ (gambleCurrentWin * gambleLastMultiplier).toFixed(2) }}
                  </span>
                  <span v-else>Wrong! You lost ${{ gambleCurrentWin.toFixed(2) }}</span>
                </div>
              </Transition>

              <!-- Guess buttons (only shown in ready phase) -->
              <template v-if="gamblePhase === 'ready'">
                <div class="gamble-section-label">Guess Color (2×)</div>
                <div class="gamble-color-row">
                  <button
                    class="gamble-btn color-btn red-btn"
                    :disabled="gamblingLoading"
                    @click="doGamble('red')"
                  >
                    ❤ RED
                  </button>
                  <button
                    class="gamble-btn color-btn black-btn"
                    :disabled="gamblingLoading"
                    @click="doGamble('black')"
                  >
                    ♠ BLACK
                  </button>
                </div>

                <div class="gamble-section-label">Guess Suit (4×)</div>
                <div class="gamble-suit-row">
                  <button
                    v-for="suit in ['clubs', 'diamonds', 'hearts', 'spades']"
                    :key="suit"
                    class="gamble-btn suit-btn"
                    :class="suit === 'hearts' || suit === 'diamonds' ? 'red' : 'black'"
                    :disabled="gamblingLoading"
                    @click="doGamble(suit)"
                  >
                    {{ suitSymbol(suit) }}
                  </button>
                </div>

                <button class="gamble-quit-btn" :disabled="gamblingLoading" @click="quitGamble">
                  Collect ${{ gambleCurrentWin.toFixed(2) }}
                </button>
              </template>

              <!-- Post-reveal action buttons -->
              <template v-if="gamblePhase === 'revealed'">
                <template v-if="gambleResult === 'win' && gambleChain + 1 < GAMBLE_MAX_CHAIN">
                  <div class="gamble-post-row">
                    <button class="gamble-btn again-btn" @click="startNextGamble">
                      {{ $t('games.video_poker.gamble_again', { amount: (gambleCurrentWin * gambleLastMultiplier).toFixed(2) }) }}
                    </button>
                    <button class="gamble-quit-btn" @click="collectGambleWin">
                      Collect ${{ (gambleCurrentWin * gambleLastMultiplier).toFixed(2) }}
                    </button>
                  </div>
                </template>
                <template v-else>
                  <button class="gamble-quit-btn full-width" @click="closeGamble">
                    {{ gambleResult === 'win' ? 'Collect & Deal Again' : 'Deal Again' }}
                  </button>
                </template>
              </template>
            </div>
          </div>
        </Transition>
      </div>

      <!-- ── Sidebar ──────────────────────────────────────────────────── -->
      <div class="game-sidebar">
        <!-- Paytable (always visible, condensed) -->
        <div class="vp-paytable">
          <div class="vp-paytable-title">PAYTABLE ({{ coins }} COIN{{ coins > 1 ? 'S' : '' }})</div>
          <div
            v-for="row in paytableRows"
            :key="row.rank"
            class="vp-pt-row"
            :class="{
              highlight: handRank === row.rank && phase === 'idle' && lastResult !== null,
              bonus: row.isBonus,
            }"
          >
            <span class="vp-pt-name">
              {{ row.name }}
              <span v-if="row.isBonus" class="bonus-badge">MAX</span>
            </span>
            <span class="vp-pt-payout">{{ row.payout }}</span>
          </div>
        </div>

        <!-- Bet Controls -->
        <div class="vp-controls">
          <!-- Multi-hand selector -->
          <label class="ctrl-label">{{ $t('games.video_poker.hands') }}</label>
          <div class="hands-selector">
            <button
              v-for="n in HAND_OPTIONS"
              :key="n"
              class="hands-btn"
              :class="{ active: numHands === n }"
              :disabled="phase !== 'idle' || gamblingActive"
              @click="numHands = n"
            >
              {{ n }}
            </button>
          </div>

          <!-- Coin selector -->
          <label class="ctrl-label">Coins</label>
          <div class="coin-selector">
            <button
              v-for="n in [1, 2, 3, 4, 5]"
              :key="n"
              class="coin-btn"
              :class="{ active: coins === n, max: n === 5 }"
              :disabled="phase !== 'idle' || gamblingActive"
              @click="coins = n"
            >
              {{ n }}
            </button>
          </div>

          <!-- Coin denomination (bet per coin) -->
          <label class="ctrl-label">Coin Value</label>
          <div class="bet-input-wrap">
            <span class="bet-currency">$</span>
            <input
              v-model="coinValueInput"
              class="bet-input"
              type="number"
              min="0.01"
              step="0.01"
              :disabled="phase !== 'idle' || gamblingActive"
              @change="clampCoinValue"
            />
          </div>

          <!-- Total bet display -->
          <div class="vp-total-bet">
            {{ $t('games.total_bet') }}: <strong>${{ totalBet.toFixed(2) }}</strong>
            <span v-if="numHands > 1" class="per-hand-hint">{{ $t('games.video_poker.per_hand_hint', { n: numHands }) }}</span>
          </div>

          <!-- Deal / Draw button -->
          <button
            class="btn-action"
            :disabled="!canAct"
            @click="onDealOrDraw"
          >
            <span v-if="loading">...</span>
            <span v-else-if="phase === 'idle'">DEAL</span>
            <span v-else-if="phase === 'draw'">DRAW</span>
          </button>

          <!-- Gamble button (shown after a win, before dealing again) -->
          <button
            v-if="canGamble"
            class="btn-gamble"
            @click="openGamble"
          >
            {{ $t('games.video_poker.double_up') }}
          </button>

          <!-- Balance -->
          <div class="balance-row">
            <span class="bal-label">{{ $t('games.balance') }}</span>
            <span class="bal-value green">${{ balanceDollars.toFixed(2) }}</span>
          </div>

          <!-- Insufficient balance warning -->
          <div v-if="insufficientBalance" class="vp-warning">
            {{ $t('games.insufficient_balance') }}
          </div>

          <!-- Error -->
          <div v-if="errorMsg" class="vp-error">{{ errorMsg }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import AppIcon from '@/components/AppIcon.vue'
import { useWalletStore } from '@/stores/wallet'

// ── Types ────────────────────────────────────────────────────────────────────

interface Card {
  rank: number
  suit: string
}

type Phase = 'idle' | 'draw'
type GamblePhase = 'ready' | 'revealing' | 'revealed'

interface PaytableRow {
  rank: string
  name: string
  payout: number
  isBonus?: boolean
}

interface HandResult {
  hand: Card[]
  hand_rank: string
  hand_name: string
  payout_amount: number
  is_win: boolean
}

interface SecondaryHandDisplay {
  cards: (Card | null)[]
  handName: string
  isWin: boolean
  payout: number
}

// ── Constants ─────────────────────────────────────────────────────────────────

const HAND_OPTIONS = [1, 3, 5, 10, 25, 50, 100] as const

// ── State ────────────────────────────────────────────────────────────────────

useI18n()
const router = useRouter()
const walletStore = useWalletStore()

/** Number of simultaneous hands to play */
const numHands = ref<number>(1)

/** Current game phase: idle = before deal / after draw; draw = awaiting hold decision */
const phase = ref<Phase>('idle')
const loading = ref(false)
const errorMsg = ref('')
const lastResult = ref<'win' | 'loss' | null>(null)

/** Cards currently displayed (5-element array; null = face-down placeholder) */
const displayHand = ref<(Card | null)[]>([null, null, null, null, null])

/** Initial hand from deal response (needed during draw phase) */
const initialHand = ref<Card[]>([])

/** Which card indices the player wants to hold (0-based, 0–4) */
const heldSet = ref<Set<number>>(new Set())

/** Final hand from draw response */
const finalHand = ref<Card[]>([])

/** All secondary hands (from multi-hand draw response) */
const secondaryHands = ref<HandResult[]>([])

/** Hand rank constant from server (e.g. "royal_flush") */
const handRank = ref<string>('')

/** Human-readable hand name (e.g. "Royal Flush") */
const handName = ref('')

/** Payout amount in dollars */
const winAmount = ref(0)

/** Whether the last draw was a win */
const isWin = ref(false)

/** Coins selected (1–5) */
const coins = ref(1)

/** Coin denomination ($ per coin) */
const coinValueInput = ref('1.00')

// ── Gamble state ──────────────────────────────────────────────────────────────

/** Maximum gamble chains per win */
const GAMBLE_MAX_CHAIN = 5

/** Whether the gamble overlay is showing */
const gamblingActive = ref(false)

/** Current sub-phase within the gamble overlay */
const gamblePhase = ref<GamblePhase>('ready')

/** Card revealed during gamble (null while face-down) */
const gambleCard = ref<Card | null>(null)

/** Color of the revealed gamble card */
const gambleCardColor = ref<'red' | 'black' | ''>('')

/** Current win amount at stake in the gamble (in dollars) */
const gambleCurrentWin = ref(0)

/** How many gambles have been completed in this chain (0-indexed) */
const gambleChain = ref(0)

/** Result of the most recent gamble */
const gambleResult = ref<'win' | 'loss' | null>(null)

/** Multiplier used in the last gamble (2 or 4) */
const gambleLastMultiplier = ref(2)

const gamblingLoading = ref(false)
const gamblingError = ref('')

// ── Paytable (JoB 9/6 reference — per-coin multipliers) ─────────────────────

const PAYTABLE_DEFINITIONS: PaytableRow[] = [
  { rank: 'royal_flush',      name: 'Royal Flush',      payout: 250 },
  { rank: 'straight_flush',   name: 'Straight Flush',   payout: 50  },
  { rank: 'four_of_a_kind',   name: 'Four of a Kind',   payout: 25  },
  { rank: 'full_house',       name: 'Full House',       payout: 9   },
  { rank: 'flush',            name: 'Flush',            payout: 6   },
  { rank: 'straight',         name: 'Straight',         payout: 4   },
  { rank: 'three_of_a_kind',  name: 'Three of a Kind',  payout: 3   },
  { rank: 'two_pair',         name: 'Two Pair',         payout: 2   },
  { rank: 'jacks_or_better',  name: 'Jacks or Better',  payout: 1   },
]

/** Paytable rows scaled to current coin count (Royal Flush bonus at 5 coins) */
const paytableRows = computed<PaytableRow[]>(() =>
  PAYTABLE_DEFINITIONS.map((row) => {
    const isRfBonus = row.rank === 'royal_flush' && coins.value === 5
    return {
      ...row,
      // Royal Flush at 5 coins pays 4000 (bonus), not linear 250×5=1250
      payout: isRfBonus ? 4000 : row.payout * coins.value,
      isBonus: isRfBonus,
    }
  }),
)

// ── Secondary hands display ───────────────────────────────────────────────────

/**
 * Secondary hands to display (all except primary hand at index 0).
 * During draw phase, shows the same initial hand as placeholders.
 */
const secondaryHandsDisplay = computed<SecondaryHandDisplay[]>(() => {
  if (numHands.value <= 1) return []

  const count = numHands.value - 1

  if (phase.value === 'draw') {
    // During draw phase: show the same initial hand for all secondary hands
    return Array.from({ length: count }, () => ({
      cards: [...initialHand.value] as (Card | null)[],
      handName: '',
      isWin: false,
      payout: 0,
    }))
  }

  if (secondaryHands.value.length > 0) {
    // After draw: show actual results for secondary hands
    return secondaryHands.value.map((h) => ({
      cards: h.hand as (Card | null)[],
      handName: h.hand_name,
      isWin: h.is_win,
      payout: h.payout_amount,
    }))
  }

  // Idle before first deal: show placeholders
  return Array.from({ length: count }, () => ({
    cards: [null, null, null, null, null] as (Card | null)[],
    handName: '',
    isWin: false,
    payout: 0,
  }))
})

// ── Computed ─────────────────────────────────────────────────────────────────

const coinValueDollars = computed(() => {
  const v = parseFloat(coinValueInput.value) || 1.0
  return Math.max(0.01, v)
})

const totalBet = computed(() => coins.value * coinValueDollars.value)

const balanceDollars = computed(() => {
  if (!walletStore.balance) return 0
  return walletStore.balance.balance / 100
})

const insufficientBalance = computed(
  () => balanceDollars.value > 0 && totalBet.value > balanceDollars.value,
)

const canAct = computed(() => {
  if (loading.value) return false
  if (gamblingActive.value) return false
  if (phase.value === 'idle') return !insufficientBalance.value && totalBet.value > 0
  return true // draw phase — always can draw
})

/** Show gamble button only after a win and while in idle phase (not gambling) */
const canGamble = computed(
  () => phase.value === 'idle' && lastResult.value === 'win' && winAmount.value > 0 && !gamblingActive.value,
)

// ── Helpers ───────────────────────────────────────────────────────────────────

function isRedSuit(suit: string): boolean {
  return suit === 'hearts' || suit === 'diamonds'
}

function suitSymbol(suit: string): string {
  return { clubs: '♣', diamonds: '♦', hearts: '♥', spades: '♠' }[suit] ?? suit
}

function rankLabel(rank: number): string {
  if (rank === 1) return 'A'
  if (rank === 11) return 'J'
  if (rank === 12) return 'Q'
  if (rank === 13) return 'K'
  return String(rank)
}

/**
 * Identity-based card key for Vue TransitionGroup animations.
 * Null (face-down) cards use position-based key.
 */
function cardKey(card: Card | null, idx: number): string {
  return card ? `${card.rank}-${card.suit}` : `empty-${idx}`
}

function isHeld(idx: number): boolean {
  return heldSet.value.has(idx)
}

function toggleHold(idx: number): void {
  const s = new Set(heldSet.value)
  if (s.has(idx)) {
    s.delete(idx)
  } else {
    s.add(idx)
  }
  heldSet.value = s
}

function clampCoinValue(): void {
  const v = parseFloat(coinValueInput.value) || 1.0
  coinValueInput.value = Math.max(0.01, v).toFixed(2)
}

// ── Gamble helpers ────────────────────────────────────────────────────────────

/** Open the gamble overlay after a win. */
function openGamble(): void {
  gambleCurrentWin.value = winAmount.value
  gambleChain.value = 0
  gamblePhase.value = 'ready'
  gambleCard.value = null
  gambleCardColor.value = ''
  gambleResult.value = null
  gambleLastMultiplier.value = 2
  gamblingActive.value = true
  gamblingError.value = ''
}

/** Quit the gamble (keep current win) — called from Collect button in ready phase. */
function quitGamble(): void {
  gamblingActive.value = false
  // win stays credited from draw endpoint
}

/** Called when player clicks "Collect" after winning a gamble round. */
function collectGambleWin(): void {
  // Update the displayed win amount to the compounded value
  winAmount.value = gambleCurrentWin.value * gambleLastMultiplier.value
  gamblingActive.value = false
}

/** Close the overlay and reset gamble state (after chain ends or loss). */
function closeGamble(): void {
  gamblingActive.value = false
  if (gambleResult.value === 'loss') {
    winAmount.value = 0
    isWin.value = false
    handName.value = ''
    lastResult.value = 'loss'
  }
}

/** Start another gamble with the updated win amount. */
function startNextGamble(): void {
  gambleCurrentWin.value = gambleCurrentWin.value * gambleLastMultiplier.value
  gambleChain.value += 1
  gamblePhase.value = 'ready'
  gambleCard.value = null
  gambleCardColor.value = ''
  gambleResult.value = null
  gamblingError.value = ''
}

/** Submit a gamble guess to the server. */
async function doGamble(guess: string): Promise<void> {
  if (gamblingLoading.value) return
  gamblingLoading.value = true
  gamblingError.value = ''
  gamblePhase.value = 'revealing'

  try {
    const res = await apiClient.post('/api/games/video-poker/gamble', {
      guess,
      amount: gambleCurrentWin.value.toFixed(2),
    })

    const data = res.data as {
      card: Card
      card_color: 'red' | 'black'
      is_win: boolean
      multiplier: number
      payout_amount: number
    }

    // Brief pause for "revealing" animation, then show result
    await new Promise((resolve) => setTimeout(resolve, 600))

    gambleCard.value = data.card
    gambleCardColor.value = data.card_color
    gambleLastMultiplier.value = data.multiplier
    gambleResult.value = data.is_win ? 'win' : 'loss'
    gamblePhase.value = 'revealed'

    walletStore.applyGameResult(gambleCurrentWin.value, data.payout_amount)
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } }
    gamblingError.value = e?.response?.data?.message ?? 'Gamble failed. Please try again.'
    gamblePhase.value = 'ready'
  } finally {
    gamblingLoading.value = false
  }
}

// ── API calls ─────────────────────────────────────────────────────────────────

async function onDealOrDraw(): Promise<void> {
  if (phase.value === 'idle') {
    await doDeal()
  } else {
    await doDraw()
  }
}

async function doDeal(): Promise<void> {
  errorMsg.value = ''
  loading.value = true
  handName.value = ''
  winAmount.value = 0
  isWin.value = false
  handRank.value = ''
  lastResult.value = null
  heldSet.value = new Set()
  secondaryHands.value = []

  try {
    const res = await apiClient.post('/api/games/video-poker/deal', {
      amount: totalBet.value.toFixed(2),
      coins: coins.value,
      num_hands: numHands.value,
    })

    const data = res.data as {
      status: string
      initial_hand: Card[]
      coins: number
      num_hands: number
    }

    initialHand.value = data.initial_hand
    // Show all 5 cards face-up; use identity keys so TransitionGroup fires enter animation
    displayHand.value = [...data.initial_hand]
    phase.value = 'draw'

    walletStore.applyGameResult(totalBet.value, 0)
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } }
    errorMsg.value = e?.response?.data?.message ?? 'Failed to deal. Please try again.'
  } finally {
    loading.value = false
  }
}

async function doDraw(): Promise<void> {
  errorMsg.value = ''
  loading.value = true

  const heldIndices = Array.from(heldSet.value)

  try {
    const res = await apiClient.post('/api/games/video-poker/draw', {
      held_indices: heldIndices,
    })

    const data = res.data as {
      status: string
      final_hand: Card[]
      held_indices: number[]
      hand_rank: string
      hand_name: string
      payout_amount: number
      is_win: boolean
      num_hands: number
      all_hands: HandResult[]
    }

    finalHand.value = data.final_hand
    handRank.value = data.hand_rank
    handName.value = data.hand_name
    winAmount.value = data.payout_amount
    isWin.value = data.is_win
    lastResult.value = data.is_win ? 'win' : 'loss'

    // Store secondary hands (all_hands[1..N])
    if (data.all_hands && data.all_hands.length > 1) {
      secondaryHands.value = data.all_hands.slice(1)
    } else {
      secondaryHands.value = []
    }

    // Animate: replace non-held cards with new cards (key change triggers TransitionGroup)
    displayHand.value = [...data.final_hand]

    phase.value = 'idle'

    walletStore.applyGameResult(0, data.payout_amount)
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } }
    errorMsg.value = e?.response?.data?.message ?? 'Failed to draw. Please try again.'
  } finally {
    loading.value = false
  }
}

// ── Reconnect on mount ────────────────────────────────────────────────────────

onMounted(async () => {
  await walletStore.fetchBalance()

  // Try to reconnect to an in-progress round
  try {
    const res = await apiClient.get('/api/games/video-poker/state')
    const data = res.data as {
      status: string
      initial_hand?: Card[]
      coins?: number
      num_hands?: number
    }

    if (data.status === 'pending_hold' && data.initial_hand) {
      initialHand.value = data.initial_hand
      displayHand.value = [...data.initial_hand]
      if (data.coins) coins.value = data.coins
      if (data.num_hands) numHands.value = data.num_hands
      phase.value = 'draw'
    }
  } catch {
    // No active round — start fresh
  }
})
</script>

<style scoped>
/* ── Layout ──────────────────────────────────────────────────────────── */
.vp-game {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.game-topbar {
  height: 48px;
  background: var(--bg-secondary);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 12px;
  flex-shrink: 0;
}

.btn-back {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: var(--radius-md);
  background: var(--bg-tertiary);
  transition: all var(--transition-fast);
  border: none;
  cursor: pointer;
}

.btn-back:hover {
  color: var(--color-on-dark, #fff);
  background: var(--bg-hover, #2a4455);
}

.game-title-bar {
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
}

.game-tag {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  padding: 2px 8px;
  border-radius: var(--radius-full);
}

.vp-tag {
  background: rgba(255, 215, 0, 0.1);
  color: var(--accent-gold);
  border: 1px solid rgba(255, 215, 0, 0.2);
}

.game-topbar-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
}

.icon-btn {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md);
  background: var(--bg-tertiary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  color: var(--text-secondary);
  transition: all var(--transition-fast);
  border: none;
}

.icon-btn:hover {
  background: var(--bg-hover, #2a4455);
  color: var(--color-on-dark, #fff);
}

.game-body {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* ── Canvas Area ──────────────────────────────────────────────────────── */
.vp-canvas-area {
  flex: 1;
  background: var(--bg-game);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  position: relative;
  overflow: auto;
}

.canvas-bg-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(0, 231, 1, 0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 231, 1, 0.025) 1px, transparent 1px);
  background-size: 40px 40px;
  pointer-events: none;
}

.canvas-bg-glow {
  position: absolute;
  pointer-events: none;
  right: -100px;
  top: -100px;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(255, 215, 0, 0.04) 0%, transparent 65%);
}

/* ── VP Display ──────────────────────────────────────────────────────── */
.vp-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  z-index: 1;
  width: 100%;
  padding: 24px;
}

/* ── Primary Hand ─────────────────────────────────────────────────────── */
.vp-primary-hand {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.vp-primary-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary);
}

.vp-hand-badge {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  font-size: 10px;
  padding: 2px 8px;
  border-radius: var(--radius-full);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

/* ── Card Row ─────────────────────────────────────────────────────────── */
.vp-cards {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.vp-card-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

/* HOLD label */
.vp-hold-label {
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--accent-primary);
  opacity: 0;
  transition: opacity var(--transition-fast);
  height: 16px;
}

.vp-hold-label.active {
  opacity: 1;
}

/* Card */
.vp-card {
  width: 86px;
  height: 124px;
  border-radius: 10px;
  background: var(--color-on-dark, #fff);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.6);
  transition: all var(--transition-fast);
  border: 2.5px solid transparent;
  position: relative;
  user-select: none;
}

.vp-card.face-down {
  background: linear-gradient(135deg, #1475e1, #0d5abf);
  border-color: rgba(255, 255, 255, 0.15);
}

.vp-card.clickable {
  cursor: pointer;
}

.vp-card.clickable:hover {
  transform: translateY(-6px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.7);
}

.vp-card.held {
  transform: translateY(-14px);
  border-color: var(--accent-primary);
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.45);
}

/* Red / black coloring */
.vp-card.red {
  color: var(--card-red, #e53935);
}

.vp-card.black {
  color: var(--roulette-black-dark, #111);
}

/* Card corners */
.vp-card-corner {
  display: flex;
  flex-direction: column;
  line-height: 1;
}

.vp-card-corner.br {
  align-self: flex-end;
  transform: rotate(180deg);
}

.vp-rank {
  font-size: 16px;
  font-weight: 900;
  font-family: var(--font-mono);
}

.vp-suit-sm {
  font-size: 12px;
  text-align: center;
}

/* Center suit symbol */
.vp-card-center {
  font-size: 38px;
  text-align: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* Card-back pattern */
.vp-card-back-pattern {
  position: absolute;
  inset: 6px;
  border-radius: 6px;
  background:
    repeating-linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.08) 0px,
      rgba(255, 255, 255, 0.08) 2px,
      transparent 2px,
      transparent 8px
    );
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* ── Hand name / win amount ───────────────────────────────────────────── */
.vp-hand-name {
  font-family: var(--font-display);
  font-size: 28px;
  color: var(--accent-gold);
  text-shadow: 0 0 30px rgba(255, 215, 0, 0.4);
  min-height: 36px;
  text-align: center;
  animation: winPop 0.35s ease both;
}

.vp-hand-name.win {
  color: var(--color-success);
  text-shadow: 0 0 30px rgba(0, 231, 1, 0.45);
}

.vp-win-amount {
  font-family: var(--font-mono);
  font-size: 22px;
  font-weight: 700;
  color: var(--color-success);
  animation: winPop 0.35s ease 0.1s both;
}

.vp-win-amount.multihand-total {
  font-size: 26px;
  border: 1px solid rgba(0, 231, 1, 0.25);
  padding: 8px 24px;
  border-radius: var(--radius-md);
  background: rgba(0, 231, 1, 0.06);
}

.vp-lose-text {
  font-size: 14px;
  color: var(--text-tertiary);
}

/* ── Multi-hand Grid ──────────────────────────────────────────────────── */
.vp-multihand-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  width: 100%;
  max-width: 900px;
}

/* Mini hand container */
.vp-mini-hand {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 6px;
  border-radius: var(--radius-sm);
  background: var(--bg-secondary);
  border: 1px solid rgba(255, 255, 255, 0.07);
  transition: all var(--transition-fast);
}

.vp-mini-hand.win {
  border-color: rgba(0, 231, 1, 0.35);
  background: rgba(0, 231, 1, 0.05);
  box-shadow: 0 0 12px rgba(0, 231, 1, 0.18);
}

.vp-mini-result {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--text-tertiary);
  text-align: center;
  min-height: 12px;
}

.vp-mini-result.win {
  color: var(--color-success);
}

.vp-mini-cards {
  display: flex;
  gap: 3px;
}

/* Mini card — size varies by hand count */
.vp-mini-card {
  border-radius: 4px;
  background: var(--color-on-dark, #fff);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  padding: 2px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
  position: relative;
  user-select: none;

  /* default size for ≤10 hands */
  width: 36px;
  height: 52px;
}

.vp-mini-card.face-down {
  background: linear-gradient(135deg, #1475e1, #0d5abf);
}

.vp-mini-card.red {
  color: var(--card-red, #e53935);
}

.vp-mini-card.black {
  color: var(--roulette-black-dark, #111);
}

.mini-rank {
  font-size: 10px;
  font-weight: 900;
  font-family: var(--font-mono);
  line-height: 1;
}

.mini-suit {
  font-size: 11px;
  line-height: 1;
}

.vp-mini-payout {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  color: var(--color-success);
  text-align: center;
}

/* Smaller cards for high hand counts */
.hands-25 .vp-mini-card,
.hands-50 .vp-mini-card,
.hands-100 .vp-mini-card {
  width: 26px;
  height: 38px;
}

.hands-25 .mini-rank,
.hands-50 .mini-rank,
.hands-100 .mini-rank {
  font-size: 8px;
}

.hands-25 .mini-suit,
.hands-50 .mini-suit,
.hands-100 .mini-suit {
  font-size: 9px;
}

.hands-25 .vp-mini-cards,
.hands-50 .vp-mini-cards,
.hands-100 .vp-mini-cards {
  gap: 2px;
}

/* ── Sidebar ─────────────────────────────────────────────────────────── */
.game-sidebar {
  width: 280px;
  background: var(--bg-secondary);
  border-left: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
}

/* ── Paytable ─────────────────────────────────────────────────────────── */
.vp-paytable {
  padding: 12px 14px 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  overflow-y: auto;
  flex-shrink: 0;
}

.vp-paytable-title {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--text-tertiary);
  margin-bottom: 8px;
}

.vp-pt-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 8px;
  border-radius: var(--radius-sm);
  transition: all var(--transition-fast);
}

.vp-pt-row.highlight {
  background: rgba(255, 215, 0, 0.15);
  border: 1px solid rgba(255, 215, 0, 0.35);
}

.vp-pt-row.bonus {
  background: rgba(255, 215, 0, 0.06);
  border: 1px solid rgba(255, 215, 0, 0.2);
}

.vp-pt-row.bonus .vp-pt-payout {
  color: var(--accent-gold);
  text-shadow: 0 0 8px rgba(255, 215, 0, 0.5);
}

.bonus-badge {
  display: inline-block;
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.05em;
  background: var(--accent-gold);
  color: var(--color-on-accent, #000);
  padding: 1px 4px;
  border-radius: 3px;
  margin-left: 4px;
  vertical-align: middle;
}

.vp-pt-name {
  font-size: 11px;
  color: var(--text-secondary);
}

.vp-pt-payout {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 700;
  color: var(--accent-gold);
}

/* ── Controls ─────────────────────────────────────────────────────────── */
.vp-controls {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 0;
  overflow-y: auto;
  flex: 1;
}

.ctrl-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary);
  margin-bottom: 6px;
  display: block;
}

/* Hands selector */
.hands-selector {
  display: flex;
  gap: 3px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.hands-btn {
  flex: 1;
  min-width: 28px;
  padding: 7px 2px;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 700;
  border-radius: var(--radius-sm);
  border: 1px solid rgba(255, 255, 255, 0.08);
  cursor: pointer;
  transition: all var(--transition-fast);
  font-family: var(--font-body);
  text-align: center;
}

.hands-btn:hover:not(:disabled) {
  background: var(--bg-hover, #2a4455);
  color: var(--color-on-dark, #fff);
}

.hands-btn.active {
  background: rgba(255, 215, 0, 0.15);
  color: var(--accent-gold);
  border-color: rgba(255, 215, 0, 0.3);
}

.hands-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Coin selector */
.coin-selector {
  display: flex;
  gap: 4px;
  margin-bottom: 14px;
}

.coin-btn {
  flex: 1;
  padding: 8px 0;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 700;
  border-radius: var(--radius-sm);
  border: 1px solid rgba(255, 255, 255, 0.08);
  cursor: pointer;
  transition: all var(--transition-fast);
  font-family: var(--font-body);
}

.coin-btn:hover:not(:disabled) {
  background: var(--bg-hover, #2a4455);
  color: var(--color-on-dark, #fff);
}

.coin-btn.active {
  background: var(--accent-primary);
  color: var(--color-on-accent, #000);
  border-color: var(--accent-primary);
}

.coin-btn.active.max {
  background: var(--accent-gold);
  border-color: var(--accent-gold);
}

.coin-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Bet input */
.bet-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.bet-currency {
  position: absolute;
  left: 10px;
  font-size: 13px;
  color: var(--text-tertiary);
  pointer-events: none;
}

.bet-input {
  background: var(--bg-primary);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md);
  padding: 10px 10px 10px 24px;
  color: var(--text-primary);
  font-family: var(--font-mono);
  font-size: 14px;
  font-weight: 600;
  width: 100%;
  transition: border-color var(--transition-fast);
}

.bet-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.bet-input:disabled {
  opacity: 0.5;
}

.vp-total-bet {
  font-size: 12px;
  color: var(--text-tertiary);
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.vp-total-bet strong {
  color: var(--text-primary);
}

.per-hand-hint {
  font-size: 10px;
  color: var(--accent-gold);
  opacity: 0.7;
}

/* Action button */
.btn-action {
  width: 100%;
  padding: 14px;
  background: var(--accent-primary);
  color: var(--color-on-accent, #000);
  font-size: 15px;
  font-weight: 800;
  border-radius: var(--radius-md);
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast);
  letter-spacing: 0.03em;
  margin-bottom: 8px;
  font-family: var(--font-body);
}

.btn-action:hover:not(:disabled) {
  background: var(--accent-primary-hover);
  box-shadow: var(--shadow-glow-green);
  transform: translateY(-1px);
}

.btn-action:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

/* Gamble button */
.btn-gamble {
  width: 100%;
  padding: 12px;
  background: linear-gradient(135deg, #d4a017, #f0c040);
  color: var(--roulette-black-dark, #1a0a00);
  font-size: 13px;
  font-weight: 800;
  border-radius: var(--radius-md);
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast);
  letter-spacing: 0.04em;
  margin-bottom: 10px;
  font-family: var(--font-body);
  text-transform: uppercase;
}

.btn-gamble:hover {
  box-shadow: 0 0 18px rgba(255, 215, 0, 0.4);
  transform: translateY(-1px);
}

/* Balance row */
.balance-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  margin-top: 4px;
}

.bal-label {
  font-size: 11px;
  color: var(--text-tertiary);
}

.bal-value.green {
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 700;
  color: var(--color-success);
}

/* Warnings / errors */
.vp-warning {
  font-size: 11px;
  color: var(--color-warning);
  margin-top: 6px;
  text-align: center;
}

.vp-error {
  font-size: 11px;
  color: var(--color-danger);
  margin-top: 6px;
  text-align: center;
}

/* ── Card deal animation ───────────────────────────────────────────────── */
.card-deal-enter-active {
  transition: all 0.35s ease;
}

.card-deal-enter-from {
  opacity: 0;
  transform: translateY(-50px) rotateY(90deg) scale(0.85);
}

.card-deal-leave-active {
  transition: all 0.2s ease;
  position: absolute;
}

.card-deal-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.85);
}

/* ── Win pop animation ────────────────────────────────────────────────── */
@keyframes winPop {
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  60% {
    transform: scale(1.08);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* ── Gamble overlay ───────────────────────────────────────────────────── */
.gamble-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  backdrop-filter: blur(4px);
}

.gamble-panel {
  background: var(--bg-secondary);
  border: 1px solid rgba(255, 215, 0, 0.25);
  border-radius: var(--radius-lg, 16px);
  padding: 28px 32px;
  min-width: 340px;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  box-shadow: 0 0 60px rgba(255, 215, 0, 0.12);
}

.gamble-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.gamble-title {
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 900;
  color: var(--accent-gold);
  letter-spacing: 0.06em;
}

.gamble-chain-badge {
  font-size: 11px;
  font-weight: 700;
  color: var(--text-tertiary);
  background: var(--bg-tertiary);
  padding: 3px 10px;
  border-radius: var(--radius-full);
  font-family: var(--font-mono);
}

.gamble-stake {
  font-size: 14px;
  color: var(--text-secondary);
}

.gamble-stake strong {
  color: var(--accent-gold);
  font-family: var(--font-mono);
  font-size: 16px;
}

/* Gamble card */
.gamble-card-area {
  perspective: 800px;
}

.gamble-card {
  width: 100px;
  height: 144px;
  border-radius: 12px;
  background: linear-gradient(135deg, #1475e1, #0d5abf);
  border: 2.5px solid rgba(255, 255, 255, 0.15);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 8px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.7);
  position: relative;
  transition: transform 0.6s ease;
  transform-style: preserve-3d;
}

.gamble-card.flip-in {
  animation: cardFlip 0.6s ease forwards;
}

.gamble-card.red {
  background: var(--color-on-dark, #fff);
  color: var(--card-red, #e53935);
  border-color: transparent;
}

.gamble-card.black {
  background: var(--color-on-dark, #fff);
  color: var(--roulette-black-dark, #111);
  border-color: transparent;
}

@keyframes cardFlip {
  0% {
    transform: rotateY(90deg) scale(0.9);
  }
  60% {
    transform: rotateY(-10deg) scale(1.02);
  }
  100% {
    transform: rotateY(0deg) scale(1);
  }
}

/* Gamble result */
.gamble-result {
  font-size: 15px;
  font-weight: 700;
  text-align: center;
  padding: 8px 16px;
  border-radius: var(--radius-md);
  animation: winPop 0.3s ease both;
}

.gamble-result.win {
  background: rgba(0, 231, 1, 0.12);
  color: var(--color-success);
  border: 1px solid rgba(0, 231, 1, 0.25);
}

.gamble-result.loss {
  background: rgba(231, 0, 0, 0.12);
  color: var(--color-danger);
  border: 1px solid rgba(231, 0, 0, 0.25);
}

/* Section label */
.gamble-section-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--text-tertiary);
  align-self: flex-start;
  margin-bottom: -8px;
}

/* Color buttons */
.gamble-color-row {
  display: flex;
  gap: 10px;
  width: 100%;
}

.gamble-btn {
  flex: 1;
  padding: 12px 0;
  font-size: 14px;
  font-weight: 800;
  border-radius: var(--radius-md);
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast);
  font-family: var(--font-body);
  letter-spacing: 0.04em;
}

.gamble-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.red-btn {
  background: linear-gradient(135deg, #c62828, #e53935);
  color: var(--color-on-dark, #fff);
}

.red-btn:hover:not(:disabled) {
  box-shadow: 0 0 16px rgba(229, 57, 53, 0.45);
  transform: translateY(-1px);
}

.black-btn {
  background: linear-gradient(135deg, #37474f, #546e7a);
  color: var(--color-on-dark, #fff);
}

.black-btn:hover:not(:disabled) {
  box-shadow: 0 0 16px rgba(84, 110, 122, 0.45);
  transform: translateY(-1px);
}

/* Suit buttons */
.gamble-suit-row {
  display: flex;
  gap: 8px;
  width: 100%;
}

.suit-btn {
  font-size: 22px;
  padding: 10px 0;
  background: var(--bg-tertiary);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-secondary);
}

.suit-btn.red {
  color: var(--card-red, #e53935);
}

.suit-btn.black {
  color: var(--text-primary);
}

.suit-btn:hover:not(:disabled) {
  background: var(--bg-hover, #2a4455);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

/* Quit / collect button */
.gamble-quit-btn {
  width: 100%;
  padding: 12px;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 700;
  border-radius: var(--radius-md);
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all var(--transition-fast);
  font-family: var(--font-body);
}

.gamble-quit-btn.full-width {
  margin-top: 4px;
}

.gamble-quit-btn:hover:not(:disabled) {
  background: var(--bg-hover, #2a4455);
  color: var(--color-on-dark, #fff);
}

.gamble-quit-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Post-reveal actions */
.gamble-post-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.again-btn {
  background: linear-gradient(135deg, #d4a017, #f0c040);
  color: var(--roulette-black-dark, #1a0a00);
}

.again-btn:hover:not(:disabled) {
  box-shadow: 0 0 16px rgba(255, 215, 0, 0.35);
  transform: translateY(-1px);
}

/* Gamble overlay transition */
.gamble-fade-enter-active,
.gamble-fade-leave-active {
  transition: opacity 0.3s ease;
}

.gamble-fade-enter-from,
.gamble-fade-leave-to {
  opacity: 0;
}

/* Result pop transition */
.result-pop-enter-active {
  transition: all 0.3s ease;
}

.result-pop-enter-from {
  opacity: 0;
  transform: scale(0.85);
}

/* ── Responsive ───────────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .game-body {
    flex-direction: column;
  }

  .game-sidebar {
    width: 100%;
    height: 320px;
    border-left: none;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    flex-direction: row;
    overflow-x: auto;
  }

  .vp-paytable {
    width: 180px;
    border-bottom: none;
    border-right: 1px solid rgba(255, 255, 255, 0.06);
    flex-shrink: 0;
  }

  .vp-controls {
    flex: 1;
  }

  .vp-card {
    width: 60px;
    height: 86px;
  }

  .vp-card-center {
    font-size: 24px;
  }

  .vp-rank {
    font-size: 12px;
  }

  .gamble-panel {
    min-width: 90vw;
    padding: 20px 18px;
  }

  /* On mobile, stack multi-hand grid */
  .vp-multihand-grid {
    max-width: 100%;
  }

  .vp-mini-card {
    width: 28px;
    height: 40px;
  }
}
</style>
